// Business/Premium probes (PRD §7) — a representative v1 subset (expanded in P2). Includes the
// two permissioned probes (camera/mic, precise location) which are opt-in and prompt explicitly;
// they only run when the engine config sets `includePermissioned` (registry gating).

import { blocked, joinNotes, measured, viaNode } from '../types';
import type { Probe, ProbeMeasurement, ProbeOutcome, Thresholds } from '../types';
import type { ProbeRegistry } from '../registry';

const Category = {
  Capabilities: 'Capabilities',
  Network: 'Network & Connectivity',
  Diagnostics: 'Diagnostics & Support',
  Media: 'Media & DRM',
  Device: 'Device & OS',
  Storage: 'Storage',
  Security: 'Security & Identity',
  Bot: 'Bot / Automation',
  Privacy: 'Privacy & Tracking',
} as const;

/** EME key systems probed for DRM support (media.drm). */
const DRM_SYSTEMS: readonly { readonly label: string; readonly keySystem: string }[] = [
  { label: 'Widevine', keySystem: 'com.widevine.alpha' },
  { label: 'PlayReady', keySystem: 'com.microsoft.playready' },
  { label: 'FairPlay', keySystem: 'com.apple.fps' },
  { label: 'ClearKey', keySystem: 'org.w3.clearkey' },
];

/** Video content types probed for decode support (media.codecs). */
const CODECS: readonly { readonly label: string; readonly type: string }[] = [
  { label: 'H.264', type: 'video/mp4; codecs="avc1.42E01E"' },
  { label: 'HEVC', type: 'video/mp4; codecs="hvc1.1.6.L93.B0"' },
  { label: 'VP9', type: 'video/webm; codecs="vp09.00.10.08"' },
  { label: 'AV1', type: 'video/mp4; codecs="av01.0.04M.08"' },
];

const info = (): ProbeOutcome => 'pass';

function scoreSupported(m: ProbeMeasurement): ProbeOutcome {
  if (m.blocked) {
    return 'warn';
  }
  return m.value === true ? 'pass' : 'warn';
}

const hardware: Probe = {
  id: 'capabilities.hardware',
  category: Category.Device,
  tier: 'business',
  permissionRequired: false,
  run: (ctx) => {
    const h = ctx.environment?.hardware;
    if (!h || (h.cores === undefined && h.memoryGb === undefined)) {
      return blocked('capabilities.hardware', 'hardware info unavailable');
    }
    const parts: string[] = [];
    if (h.cores !== undefined) {
      parts.push(`${h.cores} cores`);
    }
    if (h.memoryGb !== undefined) {
      // navigator.deviceMemory is privacy-capped at 8 GB, so 8 really means "8 or more".
      parts.push(h.memoryGb >= 8 ? '8+ GB' : `${h.memoryGb} GB`);
    }
    return measured('capabilities.hardware', parts.join(', '));
  },
  score: info,
};

const webgl2: Probe = {
  id: 'capabilities.webgl2',
  category: Category.Capabilities,
  tier: 'business',
  permissionRequired: false,
  run: (ctx) => {
    const g = ctx.environment?.graphics;
    return g
      ? measured('capabilities.webgl2', g.webgl2, g.renderer)
      : blocked('capabilities.webgl2', 'graphics info unavailable');
  },
  score: scoreSupported,
};

const clockSkew: Probe = {
  id: 'diagnostics.clock-skew',
  category: Category.Diagnostics,
  tier: 'premium',
  permissionRequired: false,
  run: async (ctx) => {
    const net = ctx.net;
    if (!net?.serverTimeUrl || !net.wallClockNow) {
      return blocked('diagnostics.clock-skew', 'server time unavailable');
    }
    const response = await net.fetch(net.serverTimeUrl, { method: 'GET', signal: ctx.signal });
    if (!response.ok) {
      return blocked('diagnostics.clock-skew', `server time HTTP ${response.status}`);
    }
    const body = (await response.json()) as { epochMs?: number };
    if (typeof body?.epochMs !== 'number') {
      return blocked('diagnostics.clock-skew', 'malformed server time');
    }
    return measured('diagnostics.clock-skew', net.wallClockNow() - body.epochMs);
  },
  // Large absolute skew breaks TLS/SSO; score against an absolute tolerance (default ±60s).
  score: (m: ProbeMeasurement, thresholds?: Thresholds): ProbeOutcome => {
    if (m.blocked || typeof m.value !== 'number') {
      return 'warn';
    }
    return Math.abs(m.value) <= (thresholds?.max ?? 60_000) ? 'pass' : 'fail';
  },
};

const cameraMic: Probe = {
  id: 'media.camera-mic',
  category: Category.Media,
  tier: 'premium',
  permissionRequired: true,
  run: async (ctx) => {
    const request = ctx.capabilities?.requestCameraMic;
    if (!request) {
      return blocked('media.camera-mic', 'camera/mic capability unavailable');
    }
    const result = await request();
    return measured('media.camera-mic', result.video && result.audio, `video=${result.video} audio=${result.audio}`);
  },
  score: (m) => (m.blocked ? 'warn' : m.value === true ? 'pass' : 'fail'),
};

const preciseLocation: Probe = {
  id: 'geo.precise',
  category: Category.Device,
  tier: 'premium',
  permissionRequired: true,
  run: async (ctx) => {
    const request = ctx.capabilities?.requestPreciseLocation;
    if (!request) {
      return blocked('geo.precise', 'geolocation capability unavailable');
    }
    const result = await request();
    return result.granted
      ? measured('geo.precise', true, result.accuracyMeters !== undefined ? `±${result.accuracyMeters}m` : undefined)
      : measured('geo.precise', false, 'permission denied');
  },
  score: scoreSupported,
};

const webrtc: Probe = {
  id: 'network.webrtc',
  category: Category.Network,
  tier: 'business',
  permissionRequired: false,
  run: async (ctx) => {
    if (!ctx.rtc) {
      return blocked('network.webrtc', 'WebRTC context unavailable');
    }
    const result = await ctx.rtc.gather();
    // A server-reflexive (srflx) candidate means the browser reached the STUN server —
    // the prerequisite for peer-to-peer calls/conferencing through NAT.
    return measured(
      'network.webrtc',
      result.reachedStun,
      result.candidateTypes.length ? result.candidateTypes.join(', ') : 'no candidates',
    );
  },
  score: (m) => (m.blocked ? 'warn' : m.value === true ? 'pass' : 'fail'),
};

const codecs: Probe = {
  id: 'media.codecs',
  category: Category.Media,
  tier: 'business',
  permissionRequired: false,
  run: async (ctx) => {
    if (!ctx.media) {
      return blocked('media.codecs', 'media capabilities unavailable');
    }
    const supported: string[] = [];
    for (const codec of CODECS) {
      try {
        if (await ctx.media.canDecode(codec.type)) {
          supported.push(codec.label);
        }
      } catch {
        // A single codec query failing shouldn't sink the probe — skip it.
      }
    }
    return measured('media.codecs', supported.length ? supported.join(', ') : 'none');
  },
  // Categorical: any decodable codec is a pass; none decodable is a warn.
  score: (m) => (m.blocked ? 'warn' : m.value && m.value !== 'none' ? 'pass' : 'warn'),
};

const thirdPartyCookies: Probe = {
  id: 'storage.third-party-cookies',
  category: Category.Storage,
  tier: 'business',
  permissionRequired: false,
  run: async (ctx) => {
    if (!ctx.storage) {
      return blocked('storage.third-party-cookies', 'storage context unavailable');
    }
    const isBlocked = await ctx.storage.thirdPartyCookiesBlocked();
    if (isBlocked === null) {
      return blocked('storage.third-party-cookies', 'indeterminate from a client-only check');
    }
    return measured(
      'storage.third-party-cookies',
      isBlocked,
      isBlocked ? 'third-party cookies blocked' : 'third-party cookies allowed',
    );
  },
  // Privacy posture: blocked third-party cookies is the good outcome.
  score: info, // privacy posture — informational by default; tenants opt in to a warn
};

const headless: Probe = {
  id: 'bot.headless',
  category: Category.Bot,
  tier: 'premium',
  permissionRequired: false,
  run: (ctx) => {
    const env = ctx.environment;
    if (!env) {
      return blocked('bot.headless', 'environment unavailable');
    }
    const signals: string[] = [];
    if (env.webdriver) {
      signals.push('navigator.webdriver');
    }
    if (/headless/i.test(env.userAgent)) {
      signals.push('headless user-agent');
    }
    const detected = signals.length > 0;
    return measured('bot.headless', detected, detected ? signals.join(', ') : 'no automation signals');
  },
  // Automation detected is a (proctoring) concern, not an outright fail; advisory warn.
  score: (m) => (m.blocked ? 'warn' : m.value === true ? 'warn' : 'pass'),
};

const drm: Probe = {
  id: 'media.drm',
  category: Category.Media,
  tier: 'premium',
  permissionRequired: false,
  run: async (ctx) => {
    const check = ctx.media?.drmSupport;
    if (!check) {
      return blocked('media.drm', 'EME unavailable');
    }
    const supported: string[] = [];
    for (const system of DRM_SYSTEMS) {
      try {
        if (await check(system.keySystem)) {
          supported.push(system.label);
        }
      } catch {
        // a single key-system query failing shouldn't sink the probe
      }
    }
    return measured('media.drm', supported.length ? supported.join(', ') : 'none');
  },
  score: (m) => (m.blocked ? 'warn' : m.value && m.value !== 'none' ? 'pass' : 'warn'),
};

const webauthn: Probe = {
  id: 'security.webauthn',
  category: Category.Security,
  tier: 'business',
  permissionRequired: false,
  run: (ctx) => {
    const env = ctx.environment;
    if (!env || env.webAuthn === undefined) {
      return blocked('security.webauthn', 'WebAuthn support unknown');
    }
    return measured('security.webauthn', env.webAuthn);
  },
  score: scoreSupported,
};

const multiDisplay: Probe = {
  id: 'device.multi-display',
  category: Category.Device,
  tier: 'business',
  permissionRequired: false,
  run: (ctx) => {
    const env = ctx.environment;
    if (!env || env.multiDisplay === undefined) {
      return blocked('device.multi-display', 'Window Management API unavailable');
    }
    return measured('device.multi-display', env.multiDisplay, env.multiDisplay ? 'multiple displays' : 'single display');
  },
  // Informational by default — a second monitor is normal. Proctoring tenants opt in to a
  // warn via a profile threshold (expected:false); the catalog default carries no constraint.
  score: info,
};

const wasm: Probe = {
  id: 'capabilities.webassembly',
  category: Category.Capabilities,
  tier: 'business',
  permissionRequired: false,
  run: (ctx) => {
    const env = ctx.environment;
    if (!env || env.webAssembly === undefined) {
      return blocked('capabilities.webassembly', 'WebAssembly support unknown');
    }
    return measured('capabilities.webassembly', env.webAssembly);
  },
  score: scoreSupported,
};

const webgpu: Probe = {
  id: 'capabilities.webgpu',
  category: Category.Capabilities,
  tier: 'business',
  permissionRequired: false,
  run: (ctx) => {
    const env = ctx.environment;
    if (!env || env.webGpu === undefined) {
      return blocked('capabilities.webgpu', 'WebGPU support unknown');
    }
    return measured('capabilities.webgpu', env.webGpu);
  },
  score: scoreSupported,
};

const workers: Probe = {
  id: 'capabilities.workers',
  category: Category.Capabilities,
  tier: 'business',
  permissionRequired: false,
  run: (ctx) => {
    const env = ctx.environment;
    if (!env || env.workers === undefined) {
      return blocked('capabilities.workers', 'worker support unknown');
    }
    // On failure, name what's missing (e.g. service workers are absent in private windows).
    const note = !env.workers && env.workersMissing && env.workersMissing.length > 0
      ? `missing ${env.workersMissing.join(', ')}`
      : undefined;
    return measured('capabilities.workers', env.workers, note);
  },
  score: scoreSupported,
};

const PERMISSION_NAMES = ['geolocation', 'notifications', 'camera', 'microphone', 'clipboard-read', 'persistent-storage'];

const permissions: Probe = {
  id: 'privacy.permissions',
  category: Category.Privacy,
  tier: 'business',
  permissionRequired: false,
  run: async (ctx) => {
    if (!ctx.permissions) {
      return blocked('privacy.permissions', 'Permissions API unavailable');
    }
    const states: string[] = [];
    for (const name of PERMISSION_NAMES) {
      const state = await ctx.permissions.query(name);
      if (state) {
        states.push(`${name}:${state}`);
      }
    }
    return states.length
      ? measured('privacy.permissions', states.join(', '))
      : blocked('privacy.permissions', 'no permissions queryable');
  },
  score: info,
};

const fingerprint: Probe = {
  id: 'privacy.fingerprint-hashes',
  category: Category.Privacy,
  tier: 'premium',
  permissionRequired: false,
  run: async (ctx) => {
    if (!ctx.fingerprint) {
      return blocked('privacy.fingerprint-hashes', 'fingerprint analysis unavailable');
    }
    const surfaces = await ctx.fingerprint.analyze();
    if (surfaces.length === 0) {
      return blocked('privacy.fingerprint-hashes', 'no fingerprint surfaces');
    }
    // PRD §16 self-aware: report only the per-surface verdict, never the raw fingerprint.
    return measured('privacy.fingerprint-hashes', surfaces.map((s) => `${s.surface}:${s.state}`).join(', '));
  },
  // Privacy posture — informational by default. Reports per-surface stable/randomized; most
  // browsers are stable (fingerprintable), so warning everyone would be noise. Tenants opt in.
  score: info,
};

const trackerBlocker: Probe = {
  id: 'privacy.tracker-blocker',
  category: Category.Privacy,
  tier: 'business',
  permissionRequired: false,
  run: async (ctx) => {
    if (!ctx.blocker) {
      return blocked('privacy.tracker-blocker', 'blocker detection unavailable');
    }
    const isBlocked = await ctx.blocker.blocked('/ads/ads.js', 'script');
    return measured('privacy.tracker-blocker', isBlocked, isBlocked ? 'ad/tracker blocker active' : 'no blocker detected');
  },
  // A blocker present is the privacy-good outcome.
  score: info, // privacy posture — informational by default; tenants opt in to a warn
};

const trackingPixel: Probe = {
  id: 'privacy.tracking-pixel',
  category: Category.Privacy,
  tier: 'business',
  permissionRequired: false,
  run: async (ctx) => {
    if (!ctx.blocker) {
      return blocked('privacy.tracking-pixel', 'blocker detection unavailable');
    }
    const isBlocked = await ctx.blocker.blocked('/pixel/track.gif', 'image');
    return measured('privacy.tracking-pixel', isBlocked, isBlocked ? 'tracking pixel blocked' : 'tracking pixel loaded');
  },
  score: info, // privacy posture — informational by default; tenants opt in to a warn
};

const offscreenCanvas: Probe = {
  id: 'capabilities.offscreen-canvas',
  category: Category.Capabilities,
  tier: 'business',
  permissionRequired: false,
  run: (ctx) => {
    const env = ctx.environment;
    if (!env || env.offscreenCanvas === undefined) {
      return blocked('capabilities.offscreen-canvas', 'OffscreenCanvas support unknown');
    }
    return measured('capabilities.offscreen-canvas', env.offscreenCanvas);
  },
  score: scoreSupported,
};

const sharedArrayBuffer: Probe = {
  id: 'capabilities.shared-array-buffer',
  category: Category.Capabilities,
  tier: 'business',
  permissionRequired: false,
  run: (ctx) => {
    const env = ctx.environment;
    if (!env || env.sharedArrayBuffer === undefined) {
      return blocked('capabilities.shared-array-buffer', 'SharedArrayBuffer support unknown');
    }
    // Absent unless the page is cross-origin isolated (COOP/COEP) — note that nuance.
    return measured(
      'capabilities.shared-array-buffer',
      env.sharedArrayBuffer,
      env.sharedArrayBuffer ? 'cross-origin isolated' : 'unavailable (needs COOP/COEP isolation)',
    );
  },
  // Informational by default — SAB needs the *page* to be cross-origin isolated, so on a
  // normal (non-isolated) site it's false for every browser; warning everyone would be noise.
  score: info,
};

/** Formats a byte count as a compact GB/MB string for quota/usage display. */
function formatBytes(bytes: number): string {
  if (bytes >= 1_000_000_000) {
    return `${Math.round((bytes / 1_000_000_000) * 10) / 10} GB`;
  }
  return `${Math.round(bytes / 1_000_000)} MB`;
}

const indexedDb: Probe = {
  id: 'storage.indexeddb',
  category: Category.Storage,
  tier: 'business',
  permissionRequired: false,
  run: async (ctx) => {
    const env = ctx.environment;
    if (!env || env.indexedDb === undefined) {
      return blocked('storage.indexeddb', 'IndexedDB support unknown');
    }
    if (!env.indexedDb) {
      // "absent" (not "unavailable") so the categorical containsAny:["available"] default
      // doesn't substring-match the negative case.
      return measured('storage.indexeddb', 'absent');
    }
    // Quota is best-effort: StorageManager.estimate() isn't everywhere — report bare
    // availability when it's missing rather than blocking.
    const estimate = ctx.storage?.estimate ? await ctx.storage.estimate() : null;
    return estimate
      ? measured('storage.indexeddb', `available, ~${formatBytes(estimate.quotaBytes)} quota`)
      : measured('storage.indexeddb', 'available');
  },
  // Categorical: any "available…" value is a pass; "unavailable" warns.
  score: (m) => (m.blocked ? 'warn' : typeof m.value === 'string' && m.value.startsWith('available') ? 'pass' : 'warn'),
};

const hdr: Probe = {
  id: 'media.hdr',
  category: Category.Media,
  tier: 'business',
  permissionRequired: false,
  run: (ctx) => {
    const env = ctx.environment;
    if (!env || env.hdr === undefined) {
      return blocked('media.hdr', 'dynamic-range support unknown');
    }
    return measured('media.hdr', env.hdr, env.colorGamut ? `${env.colorGamut} gamut` : undefined);
  },
  // Informational by default — an SDR display is normal, not a fault. Media tenants who require
  // HDR opt in via a profile threshold (expected:true).
  score: info,
};

const pictureInPicture: Probe = {
  id: 'media.picture-in-picture',
  category: Category.Media,
  tier: 'business',
  permissionRequired: false,
  run: (ctx) => {
    const env = ctx.environment;
    if (!env || env.pictureInPicture === undefined) {
      return blocked('media.picture-in-picture', 'Picture-in-Picture support unknown');
    }
    return measured('media.picture-in-picture', env.pictureInPicture);
  },
  // Informational by default — niche, and the standard-API check reads false on Firefox/iOS
  // which have their own PiP. Tenants who require it opt in via a profile threshold.
  score: info,
};

const gpuVendor: Probe = {
  id: 'capabilities.gpu-vendor',
  category: Category.Capabilities,
  tier: 'business',
  permissionRequired: false,
  run: (ctx) => {
    const vendor = ctx.environment?.graphics?.vendor;
    if (!vendor) {
      return blocked('capabilities.gpu-vendor', 'GPU vendor unavailable (masked or no WebGL)');
    }
    return measured('capabilities.gpu-vendor', vendor, ctx.environment?.graphics?.renderer);
  },
  score: info,
};

const cpuArchitecture: Probe = {
  id: 'device.cpu-architecture',
  category: Category.Device,
  tier: 'business',
  permissionRequired: false,
  run: async (ctx) => {
    if (!ctx.device) {
      return blocked('device.cpu-architecture', 'device context unavailable');
    }
    const arch = await ctx.device.cpuArchitecture();
    if (!arch) {
      return blocked('device.cpu-architecture', 'UA-CH high-entropy hints unavailable');
    }
    return measured('device.cpu-architecture', arch);
  },
  score: info,
};

const closestNode: Probe = {
  id: 'network.closest-node',
  category: Category.Network,
  tier: 'business',
  permissionRequired: false,
  run: (ctx) => {
    const node = ctx.node;
    if (!node || node.candidates.length === 0) {
      return blocked('network.closest-node', 'no API nodes configured');
    }
    if (!node.selected) {
      return blocked('network.closest-node', 'no node reachable');
    }
    const considered = node.candidates.length === 1 ? '1 node' : `${node.candidates.length} nodes`;
    return measured('network.closest-node', `${node.selected.region} (${node.selected.latencyMs} ms)`, `${considered} considered`);
  },
  score: info,
};

function scoreNumericMin(m: ProbeMeasurement, thresholds: Thresholds | undefined): ProbeOutcome {
  if (m.blocked || typeof m.value !== 'number') {
    return 'warn';
  }
  return thresholds?.min !== undefined ? (m.value >= thresholds.min ? 'pass' : 'fail') : 'pass';
}

function scoreNumericMax(m: ProbeMeasurement, thresholds: Thresholds | undefined): ProbeOutcome {
  if (m.blocked || typeof m.value !== 'number') {
    return 'warn';
  }
  return thresholds?.max !== undefined ? (m.value <= thresholds.max ? 'pass' : 'fail') : 'pass';
}

// Adaptive upload sizing (mirrors the download probe): a small pilot POST estimates the
// line, the main POST is sized to run ~this long. fetch() can't stream-time uploads, so the
// warm-up ping's RTT is subtracted from the round-trip instead — on short transfers a whole
// RTT of request/response overhead otherwise reads as "transfer time".
const UPLOAD_BUDGET_MS = 2_500;
const UPLOAD_PILOT_BYTES = 262_144;
const UPLOAD_MIN_BYTES = 524_288;
const UPLOAD_MAX_BYTES = 12_000_000; // comfortably under the server's 16 MB cap

const upload: Probe = {
  id: 'network.upload',
  category: Category.Network,
  tier: 'business',
  permissionRequired: false,
  heavy: true,
  run: async (ctx) => {
    const net = ctx.net;
    if (!net?.uploadUrl || !net.uploadBytes) {
      return blocked('network.upload', 'upload endpoint unavailable');
    }
    // Warm-up primes DNS/TLS/TCP; timing it measures the RTT we subtract below.
    let rttMs = 0;
    try {
      const pingStart = net.now();
      await net.fetch(net.pingUrl, { method: 'GET', signal: ctx.signal });
      rttMs = net.now() - pingStart;
    } catch {
      // a failed warm-up is fine — the measured POSTs below are what count
    }

    // One timed POST: elapsed round-trip minus the RTT (floored at half the raw elapsed so
    // a jittery ping can never produce a negative or absurd window).
    const timedPost = async (bytes: number): Promise<number | null> => {
      const start = net.now();
      const response = await net.fetch(net.uploadUrl!, { method: 'POST', body: new Uint8Array(bytes), signal: ctx.signal });
      if (!response.ok) {
        return null;
      }
      await response.json().catch(() => undefined); // drain so the round trip is complete
      const elapsed = net.now() - start;
      return elapsed <= 0 ? null : Math.max(elapsed - rttMs, elapsed / 2);
    };

    const pilotMs = await timedPost(UPLOAD_PILOT_BYTES);
    if (pilotMs === null) {
      return blocked('network.upload', 'upload endpoint unreachable');
    }
    const pilotMbps = (UPLOAD_PILOT_BYTES * 8) / (pilotMs * 1000);
    const mainBytes = Math.min(
      UPLOAD_MAX_BYTES,
      Math.max(UPLOAD_MIN_BYTES, Math.round(pilotMbps * UPLOAD_BUDGET_MS * 125)),
    );
    const mainMs = await timedPost(mainBytes);
    if (mainMs === null) {
      return blocked('network.upload', 'invalid timing');
    }
    const mbps = (mainBytes * 8) / (mainMs * 1000);
    return measured(
      'network.upload',
      Math.round(mbps * 10) / 10,
      joinNotes(`${(mainBytes / 1_000_000).toFixed(1)} MB sampled`, viaNode(ctx)),
    );
  },
  score: (m, thresholds) => scoreNumericMin(m, thresholds),
};

// ---- Sustained throughput (Premium): duration-based, multi-stream, parameter-driven ----
// Defaults mirror the catalog's DefaultParameters; the server delivers effective (profile
// override ?? catalog default) parameters via ctx.params, already clamped server-side. The
// hard byte caps bound node egress no matter how fast the line is.
const SUSTAINED_DOWN_DEFAULTS = { durationMs: 8_000, streams: 4, chunkBytes: 8_000_000, maxTotalBytes: 200_000_000 };
const SUSTAINED_UP_DEFAULTS = { durationMs: 8_000, streams: 3, chunkBytes: 4_000_000, maxTotalBytes: 60_000_000 };

function sustainedParams(
  raw: Record<string, unknown> | undefined,
  defaults: { durationMs: number; streams: number; chunkBytes: number; maxTotalBytes: number },
) {
  const num = (v: unknown, fallback: number) => (typeof v === 'number' && Number.isFinite(v) && v > 0 ? v : fallback);
  return {
    durationMs: num(raw?.durationMs, defaults.durationMs),
    streams: Math.min(6, Math.max(1, Math.round(num(raw?.streams, defaults.streams)))),
    chunkBytes: num(raw?.chunkBytes, defaults.chunkBytes),
    maxTotalBytes: num(raw?.maxTotalBytes, defaults.maxTotalBytes),
  };
}

/**
 * Runs N concurrent chunk-transfer workers for the duration (or until the byte cap), skipping
 * each worker's first chunk (TCP ramp), then aggregates throughput over the post-ramp wall
 * clock. transfer() resolves the chunk's counted bytes, or null for a failed chunk — a worker
 * survives ONE failed chunk (transient reset / brief 429) and stops on the second. If no
 * post-ramp chunk completed but ramp chunks did, the result falls back to those with
 * short: true (a conservative estimate — the ramp is included — beats reporting nothing).
 */
async function runSustained(
  now: () => number,
  p: { durationMs: number; streams: number; chunkBytes: number; maxTotalBytes: number },
  transfer: (chunkBytes: number) => Promise<number | null>,
): Promise<{ mbps: number; bytes: number; windowMs: number; short?: boolean } | null> {
  const t0 = now();
  let requested = 0;
  const samples: { bytes: number; start: number; end: number }[] = [];
  const rampSamples: { bytes: number; end: number }[] = [];

  const worker = async () => {
    let first = true;
    let retried = false;
    while (now() - t0 < p.durationMs && requested < p.maxTotalBytes) {
      requested += p.chunkBytes;
      const start = now();
      const bytes = await transfer(p.chunkBytes);
      if (bytes === null) {
        if (retried) {
          return; // second failure: the path is genuinely down for this worker
        }
        retried = true;
        continue;
      }
      if (first) {
        first = false; // per-worker ramp discard (kept for the short-sample fallback)
        rampSamples.push({ bytes, end: now() });
        continue;
      }
      samples.push({ bytes, start, end: now() });
    }
  };
  await Promise.all(Array.from({ length: p.streams }, worker));

  if (samples.length === 0) {
    // Nothing survived the ramp discard (saturated line, tight window). Estimate from the
    // completed ramp chunks rather than reporting nothing — flagged short so the note says so.
    if (rampSamples.length === 0) {
      return null;
    }
    const last = Math.max(...rampSamples.map((s) => s.end));
    const bytes = rampSamples.reduce((sum, s) => sum + s.bytes, 0);
    const windowMs = last - t0;
    if (bytes === 0 || windowMs <= 0) {
      return null;
    }
    return { mbps: (bytes * 8) / (windowMs * 1000), bytes, windowMs, short: true };
  }
  // Measurement window: from the first counted chunk's START to the last counted chunk's end,
  // so the window covers exactly the time the counted transfers occupied the line. Anchoring
  // on a fixed ramp cutoff understated slow lines badly: a ~38 Mbps line spends ~6-7 s just
  // completing the (discarded) 8 MB ramp chunks, and dividing the counted bytes by a window
  // that included that ramp time reported ~25. Start-anchoring is also correct for fast
  // cap-limited runs (the first counted chunk starts right after its worker's ramp ends).
  const windowStart = Math.min(...samples.map((s) => s.start));
  const windowEnd = Math.max(...samples.map((s) => s.end));
  const windowMs = windowEnd - windowStart;
  const bytes = samples.reduce((sum, s) => sum + s.bytes, 0);
  if (bytes === 0 || windowMs <= 0) {
    return null;
  }
  return { mbps: (bytes * 8) / (windowMs * 1000), bytes, windowMs };
}

const sustainedNote = (r: { bytes: number; windowMs: number; short?: boolean }, streams: number): string =>
  `${(r.bytes / 1_000_000).toFixed(0)} MB over ${(r.windowMs / 1000).toFixed(1)} s, ${streams} streams` +
  (r.short ? ' (short sample)' : '');

// ---- Adaptive chunk sizing: fit ramp + several counted chunks in the duration ----
// The configured chunkBytes is sized for fast lines; on a ~30 Mbps line, 4 concurrent 8 MB
// ramp chunks alone take longer than the whole test, so every byte got discarded and the
// short-sample fallback under-read the line (false FAILs against a min-25 threshold). A small
// timed pilot estimates the rate and shrinks the chunk so each stream completes its ramp plus
// ~4 counted chunks inside the window. The configured value stays the CEILING (the tenant's
// parameter and the byte caps are unchanged); the floor keeps chunks big enough to measure.
const SUSTAINED_PILOT_BYTES = 512_000;
const SUSTAINED_MIN_CHUNK_BYTES = 250_000;
const SUSTAINED_CHUNKS_PER_STREAM = 5; // 1 ramp + ~4 counted

function adaptChunkBytes(
  pilotBytesPerSecond: number | null,
  p: { durationMs: number; streams: number; chunkBytes: number },
): number {
  if (pilotBytesPerSecond === null || !Number.isFinite(pilotBytesPerSecond) || pilotBytesPerSecond <= 0) {
    return p.chunkBytes; // no pilot signal: keep the configured size
  }
  const perStreamBudget = (pilotBytesPerSecond * p.durationMs) / 1000 / (p.streams * SUSTAINED_CHUNKS_PER_STREAM);
  return Math.max(SUSTAINED_MIN_CHUNK_BYTES, Math.min(p.chunkBytes, Math.round(perStreamBudget)));
}

const downloadSustained: Probe = {
  id: 'network.download-sustained',
  category: Category.Network,
  tier: 'premium',
  permissionRequired: false,
  heavy: true,
  run: async (ctx) => {
    const net = ctx.net;
    if (!net?.downloadTimed) {
      return blocked('network.download-sustained', 'streaming context unavailable');
    }
    const p = sustainedParams(ctx.params?.['network.download-sustained'], SUSTAINED_DOWN_DEFAULTS);
    try {
      await net.fetch(net.pingUrl, { method: 'GET', signal: ctx.signal });
    } catch { /* warm-up only */ }
    let pilotBps: number | null = null;
    try {
      const pilot = await net.downloadTimed(SUSTAINED_PILOT_BYTES, ctx.signal);
      if (pilot && pilot.elapsedMs > 0) {
        pilotBps = (pilot.receivedBytes || SUSTAINED_PILOT_BYTES) / (pilot.elapsedMs / 1000);
      }
    } catch { /* no pilot signal: keep the configured chunk size */ }
    const sized = { ...p, chunkBytes: adaptChunkBytes(pilotBps, p) };
    const result = await runSustained(net.now, sized, async (chunk) => {
      const r = await net.downloadTimed!(chunk, ctx.signal);
      return r ? r.receivedBytes || chunk : null;
    });
    if (!result) {
      return blocked('network.download-sustained', 'no sustained samples');
    }
    return measured('network.download-sustained', Math.round(result.mbps * 10) / 10, joinNotes(sustainedNote(result, p.streams), viaNode(ctx)));
  },
  score: (m, thresholds) => scoreNumericMin(m, thresholds),
};

const uploadSustained: Probe = {
  id: 'network.upload-sustained',
  category: Category.Network,
  tier: 'premium',
  permissionRequired: false,
  heavy: true,
  run: async (ctx) => {
    const net = ctx.net;
    if (!net?.uploadUrl) {
      return blocked('network.upload-sustained', 'upload endpoint unavailable');
    }
    const p = sustainedParams(ctx.params?.['network.upload-sustained'], SUSTAINED_UP_DEFAULTS);
    try {
      await net.fetch(net.pingUrl, { method: 'GET', signal: ctx.signal });
    } catch { /* warm-up only */ }
    let pilotBps: number | null = null;
    try {
      const start = net.now();
      const pilot = await net.fetch(net.uploadUrl, { method: 'POST', body: new Uint8Array(SUSTAINED_PILOT_BYTES), signal: ctx.signal });
      if (pilot.ok) {
        await pilot.json().catch(() => undefined);
        const elapsedMs = net.now() - start;
        if (elapsedMs > 0) {
          pilotBps = SUSTAINED_PILOT_BYTES / (elapsedMs / 1000);
        }
      }
    } catch { /* no pilot signal: keep the configured chunk size */ }
    const sized = { ...p, chunkBytes: adaptChunkBytes(pilotBps, p) };
    const result = await runSustained(net.now, sized, async (chunk) => {
      const response = await net.fetch(net.uploadUrl!, { method: 'POST', body: new Uint8Array(chunk), signal: ctx.signal });
      if (!response.ok) {
        return null;
      }
      await response.json().catch(() => undefined);
      return chunk;
    });
    if (!result) {
      return blocked('network.upload-sustained', 'no sustained samples');
    }
    return measured('network.upload-sustained', Math.round(result.mbps * 10) / 10, joinNotes(sustainedNote(result, p.streams), viaNode(ctx)));
  },
  score: (m, thresholds) => scoreNumericMin(m, thresholds),
};

const JITTER_SAMPLES = 6;

const jitter: Probe = {
  id: 'network.jitter',
  category: Category.Network,
  tier: 'business',
  permissionRequired: false,
  run: async (ctx) => {
    const net = ctx.net;
    if (!net) {
      return blocked('network.jitter', 'network context unavailable');
    }
    const samples: number[] = [];
    for (let i = 0; i < JITTER_SAMPLES; i++) {
      const start = net.now();
      try {
        const response = await net.fetch(net.pingUrl, { method: 'GET', signal: ctx.signal });
        if (response.ok) {
          samples.push(net.now() - start);
        }
      } catch {
        // a dropped sample shouldn't sink the probe — keep going
      }
    }
    if (samples.length < 2) {
      return blocked('network.jitter', 'insufficient ping samples');
    }
    const mean = samples.reduce((a, b) => a + b, 0) / samples.length;
    const variance = samples.reduce((a, b) => a + (b - mean) ** 2, 0) / samples.length;
    // Jitter = standard deviation of the round-trip times (ms).
    return measured('network.jitter', Math.round(Math.sqrt(variance) * 10) / 10, viaNode(ctx));
  },
  score: (m, thresholds) => scoreNumericMax(m, thresholds),
};

const LOSS_SAMPLES = 20;

const loss: Probe = {
  id: 'network.loss',
  category: Category.Network,
  tier: 'business',
  permissionRequired: false,
  run: async (ctx) => {
    const net = ctx.net;
    if (!net) {
      return blocked('network.loss', 'network context unavailable');
    }
    // HTTP request-loss approximation (not true UDP packet loss — App Service can't serve UDP).
    let failed = 0;
    for (let i = 0; i < LOSS_SAMPLES; i++) {
      try {
        const response = await net.fetch(net.pingUrl, { method: 'GET', signal: ctx.signal });
        if (!response.ok) {
          failed++;
        }
      } catch {
        failed++;
      }
    }
    return measured('network.loss', Math.round((failed / LOSS_SAMPLES) * 1000) / 10, joinNotes('HTTP request loss (approx.)', viaNode(ctx)));
  },
  score: (m, thresholds) => scoreNumericMax(m, thresholds),
};

const reachability: Probe = {
  id: 'network.reachability',
  category: Category.Network,
  tier: 'business',
  permissionRequired: false,
  run: async (ctx) => {
    if (!ctx.reachability) {
      return blocked('network.reachability', 'reachability context unavailable');
    }
    const results = await ctx.reachability.check();
    if (results.length === 0) {
      return blocked('network.reachability', 'no targets probed');
    }
    const reachable = results.filter((r) => r.reachable).map((r) => r.target);
    return measured(
      'network.reachability',
      reachable.length ? reachable.join(', ') : 'none',
      `${reachable.length}/${results.length} reachable`,
    );
  },
  // Basic connectivity (any HTTPS target reachable) is a pass; nothing reachable warns.
  score: (m) => (m.blocked || typeof m.value !== 'string' ? 'warn' : /https/.test(m.value) ? 'pass' : 'warn'),
};

const fingerprintEntropy: Probe = {
  id: 'privacy.fingerprint-entropy',
  category: Category.Privacy,
  tier: 'premium',
  permissionRequired: false,
  run: async (ctx) => {
    if (!ctx.entropy) {
      return blocked('privacy.fingerprint-entropy', 'entropy service unavailable');
    }
    const result = await ctx.entropy.measure();
    if (!result) {
      return blocked('privacy.fingerprint-entropy', 'entropy measurement unavailable');
    }
    return measured('privacy.fingerprint-entropy', result.bits, `≈1 in ${result.oneInN} visitors`);
  },
  // Privacy posture — informational by default. Reports uniqueness (bits / 1-in-N); most
  // visitors are somewhat unique, so warning by default would be noise. Tenants opt in to a max.
  score: info,
};

const isp: Probe = {
  id: 'network.isp',
  category: Category.Network,
  tier: 'business',
  permissionRequired: false,
  run: async (ctx) => {
    if (!ctx.isp) {
      return blocked('network.isp', 'ISP context unavailable');
    }
    const result = await ctx.isp.lookup();
    if (!result?.organization) {
      return blocked('network.isp', 'ISP lookup unavailable');
    }
    const value = result.asn ? `${result.organization} (AS${result.asn})` : result.organization;
    return measured('network.isp', value);
  },
  // Informational: the ISP is identity, not a verdict; tenants opt in to a match via thresholds.
  score: info,
};

const ipDetails: Probe = {
  id: 'network.ip-details',
  category: Category.Network,
  tier: 'business',
  permissionRequired: false,
  run: async (ctx) => {
    if (!ctx.ip) {
      return blocked('network.ip-details', 'IP context unavailable');
    }
    const result = await ctx.ip.details();
    if (!result?.ip) {
      return blocked('network.ip-details', 'connection details unavailable');
    }
    // Reverse-DNS name (or the bare address), coarse location, source port, IP version.
    // The visitor sees the full details live; the server masks the address (and the
    // PTR name embedding it) at ingestion before anything is stored (PRD §16).
    const location = [result.city, result.region, result.country].filter(Boolean).join(', ');
    const parts = [
      result.reverseDns ?? result.ip,
      location || null,
      result.port !== null ? `port ${result.port}` : null,
      result.version,
    ].filter(Boolean);
    return measured('network.ip-details', parts.join(', '));
  },
  // Informational: connection identity, not a verdict.
  score: info,
};

/** Business/Premium probe set (representative v1 subset). */
export const businessProbes: readonly Probe[] = [
  hardware,
  webgl2,
  clockSkew,
  cameraMic,
  preciseLocation,
  webrtc,
  codecs,
  thirdPartyCookies,
  headless,
  drm,
  webauthn,
  multiDisplay,
  wasm,
  webgpu,
  workers,
  permissions,
  fingerprint,
  trackerBlocker,
  trackingPixel,
  offscreenCanvas,
  sharedArrayBuffer,
  indexedDb,
  hdr,
  pictureInPicture,
  gpuVendor,
  cpuArchitecture,
  closestNode,
  upload,
  downloadSustained,
  uploadSustained,
  jitter,
  loss,
  reachability,
  isp,
  ipDetails,
  fingerprintEntropy,
];

/** Registers every business/premium probe into the given registry. */
export function registerBusinessProbes(target: ProbeRegistry): void {
  for (const probe of businessProbes) {
    target.register(probe);
  }
}
